import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import type { AxiosError } from 'axios';
import { AppInsightsProvider } from './services/appInsights';
import { AppInsightsErrorBoundary } from './components/common/AppInsightsErrorBoundary';
import { MixpanelProvider } from './services/mixpanel';
import App from './App';
import './styles/index.css';

// Solo reintentamos caídas transitorias de infraestructura. Un 4xx (auth,
// validación, no encontrado) NUNCA se reintenta: reintentarlo no lo arregla.
const TRANSIENT_STATUS = new Set([502, 503, 504, 429]);

function isTransientError(error: unknown): boolean {
  const status = (error as AxiosError | undefined)?.response?.status;
  if (status !== undefined) {
    return TRANSIENT_STATUS.has(status);
  }
  // Sin response = error de red. Reintentamos salvo timeout (ya esperó 65s):
  // reintentar un timeout solo multiplicaría la espera.
  const code = (error as AxiosError | undefined)?.code;
  return code !== 'ECONNABORTED' && code !== 'ETIMEDOUT';
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minuto
      // Reintento inteligente: hasta 3 intentos ante 5xx/429/errores de red
      // (cubre los blips de Redis → 503), nunca ante 4xx.
      retry: (failureCount, error) => isTransientError(error) && failureCount < 3,
      // Backoff exponencial con tope: ~1s, 2s, 4s (máx 8s) — le da tiempo a
      // Redis a recuperarse sin martillar el backend.
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      refetchOnWindowFocus: false,
    },
    mutations: {
      // Las mutations NO se auto-reintentan: reintentar una operación no
      // idempotente (p. ej. emisión de factura) podría duplicarla. La emisión
      // tiene su propio retry seguro reusando el x-idempotency-key.
      retry: false,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <AppInsightsProvider>
      <AppInsightsErrorBoundary>
        <MixpanelProvider>
          <QueryClientProvider client={queryClient}>
            <App />
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </MixpanelProvider>
      </AppInsightsErrorBoundary>
    </AppInsightsProvider>
  </React.StrictMode>,
);
